import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { isEqual } from 'lodash';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import {
  DEFAULT_CLIENT_PAUSE,
  DEFAULT_CLIENT_PAUSE_PROJECT,
} from '@src/constants/tts';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import PunctuationList from '../VoicePresetDialog/PunctuationList';

const ClientPause = ({ open, onClose, toolbarV3, isProject }) => {
  const [loadingData, setLoadingData] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const defaultClientPause = isProject
    ? DEFAULT_CLIENT_PAUSE_PROJECT
    : DEFAULT_CLIENT_PAUSE;
  const [clientPause, setClientPause] = useState(defaultClientPause);

  const dispatch = useDispatch();

  const { t, i18n } = useTranslation();
  const isVietnamese = i18n.language === 'vi';
  const { relateDataRequest, ttsPreset, synthesisRequest } = useSelector(
    (state) => state.synthesisRequest,
  );

  const fetchClientPause = async () => {
    if (ttsPreset) {
      setClientPause(ttsPreset?.clientPause);
      return;
    }
    setLoadingData(true);
    const data = await apis.clientPause.getClientPause();
    if (data?.status) {
      const { clientPause: dataClientPause } = data.result || {};
      if (dataClientPause) {
        const secClientPause = Object.keys(defaultClientPause).reduce(
          (acc, key) => {
            acc[key] = dataClientPause[key] ?? defaultClientPause[key];
            return acc;
          },
          {},
        );
        if (toolbarV3) {
          setClientPause(synthesisRequest?.clientPause || secClientPause);
        } else {
          setClientPause(secClientPause);
        }
      }
    }
    setLoadingData(false);
  };

  const handleClose = () => {
    setLoadingData(false);
    onClose();
  };

  const handleChangeSeconds = (key, sec) =>
    setClientPause({
      ...clientPause,
      [key]: Number(Number(sec).toFixed(2)),
    });

  const handleSave = async (data) => {
    setLoadingSave(true);
    const response = await apis.clientPause.updateClientPause(data);
    if (toolbarV3) {
      dispatch(
        actions.synthesisRequest.updateSynthesisConfig({
          ...synthesisRequest,
          clientPause: data,
        }),
      );
    }
    if (!isEqual(relateDataRequest?.clientPause, data)) {
      dispatch(
        actions.synthesisRequest.updateDataRelatedRequest({
          ...relateDataRequest,
          clientPause: {},
        }),
      );
    }
    // Reset current TTS Preset if client pause is changed
    if (!isEqual(ttsPreset?.clientPause, data) && ttsPreset) {
      dispatch(actions.synthesisRequest.updateCurrentTTSPreset(null));
    }
    setLoadingSave(false);
    if (!response?.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'pauseSettingsFailure',
        }),
      );
      return;
    }
    dispatch(
      actions.noti.push({
        severity: 'success',
        message: 'pauseSettingsSuccess',
      }),
    );
    onClose();
  };

  const handleReset = () => setClientPause(defaultClientPause);

  useEffect(() => {
    fetchClientPause();
  }, [open]);

  const renderContent = () => (
    <PunctuationList
      clientPause={clientPause}
      handleChangeSeconds={handleChangeSeconds}
      isVietnamese={isVietnamese}
      isProject={isProject}
    />
  );

  const renderAction = () => (
    <Box display="flex" gap={1} alignItems="center">
      <CommonButton
        variant="outlined"
        data-id="reset-punct-duration"
        onClick={handleReset}
        customStyle={{
          height: '40px',
          borderRadius: '8px',
          border: `1px solid ${COLOR.secondary[16]}`,
          '&:focus': {
            backgroundColor: 'transparent',
          },
        }}
        textProps={{
          color: COLOR.indigo.default,
          variantText: 'body-14-semibold',
        }}
        disabled={loadingData || loadingSave}
      >
        {t('setDefaultV2')}
      </CommonButton>
      <CommonButton
        variant="contained"
        data-id="save-punct-duration"
        onClick={() => handleSave(clientPause)}
        disabled={loadingData || loadingSave}
        customStyle={{
          height: '40px',
          borderRadius: '8px',
        }}
        textProps={{
          color: COLOR.indigo.default,
          variantText: 'body-14-semibold',
        }}
      >
        {t('saveName')}
      </CommonButton>
    </Box>
  );

  return (
    <CommonDialog
      open={open}
      width="640px"
      onClose={handleClose}
      title={t('pauseSettings')}
      contentComponent={renderContent()}
      actionComponent={renderAction()}
    />
  );
};

export default ClientPause;
